import { useCallback, useEffect, useMemo, useState } from 'react';
import { getThemeDiscussionPalette } from '../../config/themes';
import { MarkdownPreview } from './NodeContent';

const MAX_RENDERED_MARKDOWN_CHARACTERS = 250_000;
const MAX_MARKDOWN_BYTES = 2 * 1024 * 1024;

function AttachmentViewerModal({
  attachment,
  content,
  objectUrl,
  isLoading,
  error,
  isDarkMode,
  theme,
  onClose,
  onDownload,
  canEdit = false,
  onSave = null,
}) {
  const [markdownMode, setMarkdownMode] = useState('preview');
  const [savedContent, setSavedContent] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const palette = getThemeDiscussionPalette(theme, isDarkMode);
  const isMarkdown = attachment?.kind === 'markdown';
  const markdownContent = typeof content === 'string' ? content : '';
  const displayedContent = isMarkdown && canEdit ? draftContent : markdownContent;
  const previewWasTruncated = isMarkdown && displayedContent.length > MAX_RENDERED_MARKDOWN_CHARACTERS;
  const previewContent = previewWasTruncated
    ? displayedContent.slice(0, MAX_RENDERED_MARKDOWN_CHARACTERS)
    : displayedContent;
  const draftBytes = useMemo(() => (
    typeof TextEncoder === 'undefined'
      ? draftContent.length
      : new TextEncoder().encode(draftContent).length
  ), [draftContent]);
  const isDirty = isMarkdown && canEdit && draftContent !== savedContent;
  const saveDisabled = (
    !isDirty
    || isSaving
    || draftBytes > MAX_MARKDOWN_BYTES
    || typeof onSave !== 'function'
  );

  useEffect(() => {
    setMarkdownMode('preview');
    setSavedContent(markdownContent);
    setDraftContent(markdownContent);
    setSaveError('');
    setIsSaving(false);
  }, [attachment?.assetId, markdownContent]);

  const requestClose = useCallback(() => {
    if (
      isDirty
      && typeof window !== 'undefined'
      && !window.confirm('Discard your unsaved Markdown changes?')
    ) {
      return;
    }
    onClose?.();
  }, [isDirty, onClose]);

  const saveMarkdown = useCallback(async () => {
    if (saveDisabled) return;
    setIsSaving(true);
    setSaveError('');
    try {
      await onSave(draftContent);
      setSavedContent(draftContent);
      setMarkdownMode('preview');
    } catch (saveFailure) {
      setSaveError(saveFailure?.message || 'Could not save this Markdown file.');
    } finally {
      setIsSaving(false);
    }
  }, [draftContent, onSave, saveDisabled]);

  const cancelMarkdownEdit = useCallback(() => {
    setDraftContent(savedContent);
    setSaveError('');
    setMarkdownMode('preview');
  }, [savedContent]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        requestClose();
        return;
      }
      if (
        isMarkdown
        && canEdit
        && (event.ctrlKey || event.metaKey)
        && String(event.key).toLowerCase() === 's'
      ) {
        event.preventDefault();
        saveMarkdown();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canEdit, isMarkdown, requestClose, saveMarkdown]);

  useEffect(() => {
    if (!isDirty || typeof window === 'undefined') return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return (
    <div
      className="attachment-viewer-backdrop"
      role="presentation"
      data-export-ignore="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        className="attachment-viewer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="attachment-viewer-title"
        style={{
          background: palette.panel,
          color: palette.text,
          borderColor: palette.border,
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="attachment-viewer__header" style={{ borderColor: palette.border }}>
          <div id="attachment-viewer-title" className="attachment-viewer__title">
            {attachment?.filename || (isMarkdown ? 'Markdown file' : 'Image')}
          </div>
          {isMarkdown && !isLoading && !error && (
            <div
              className="attachment-viewer__mode-switch"
              role="group"
              aria-label="Markdown display mode"
              style={{ background: palette.raised }}
            >
              {['preview', canEdit ? 'edit' : 'markdown'].map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  aria-pressed={markdownMode === candidate}
                  onClick={() => setMarkdownMode(candidate)}
                  style={{
                    background: markdownMode === candidate ? palette.accent : 'transparent',
                    color: markdownMode === candidate ? palette.accentText : palette.text,
                  }}
                >
                  {candidate === 'preview' ? 'Preview' : (canEdit ? 'Edit' : 'Markdown')}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="attachment-viewer__close"
            aria-label="Close attachment"
            onClick={requestClose}
            style={{ color: palette.text }}
          >
            ×
          </button>
        </header>

        <div className="attachment-viewer__body">
          {isLoading ? (
            <div role="status" className="attachment-viewer__message">Loading attachment…</div>
          ) : error ? (
            <div role="alert" className="attachment-viewer__message">{error}</div>
          ) : isMarkdown ? (
            markdownMode === 'preview' ? (
              <article className="attachment-viewer__markdown-preview">
                {previewWasTruncated && (
                  <p className="attachment-viewer__notice" role="status">
                    This file is large, so the rendered preview is shortened. Switch to Markdown to view all of it.
                  </p>
                )}
                <MarkdownPreview source={previewContent || ''} />
              </article>
            ) : canEdit ? (
              <div className="attachment-viewer__markdown-editor-wrap">
                <textarea
                  className="attachment-viewer__markdown-editor"
                  aria-label="Markdown source"
                  autoFocus
                  value={draftContent}
                  onChange={(event) => {
                    setDraftContent(event.target.value);
                    setSaveError('');
                  }}
                  spellCheck="true"
                  style={{
                    background: palette.input,
                    color: palette.text,
                    borderColor: palette.border,
                  }}
                />
                <div className="attachment-viewer__editor-status">
                  <span>{draftBytes.toLocaleString()} / {MAX_MARKDOWN_BYTES.toLocaleString()} bytes</span>
                  {draftBytes > MAX_MARKDOWN_BYTES && (
                    <span role="alert">Markdown files can be up to 2 MB.</span>
                  )}
                  {saveError && <span role="alert">{saveError}</span>}
                </div>
              </div>
            ) : (
              <pre className="attachment-viewer__markdown-source" style={{ background: palette.input }}>
                <code>{markdownContent}</code>
              </pre>
            )
          ) : objectUrl ? (
            <img
              className="attachment-viewer__image"
              src={objectUrl}
              alt={attachment?.filename || 'Attached image'}
            />
          ) : null}
        </div>

        <footer className="attachment-viewer__footer" style={{ borderColor: palette.border }}>
          <span>
            {isMarkdown
              ? canEdit
                ? isSaving
                  ? 'Saving Markdown…'
                  : isDirty
                    ? 'Unsaved Markdown changes'
                    : 'Markdown is saved'
                : 'Markdown preview is read only.'
              : 'Full image preview'}
          </span>
          {isMarkdown && canEdit && isDirty && (
            <>
              <button
                type="button"
                onClick={cancelMarkdownEdit}
                disabled={isSaving}
                style={{ background: palette.raised, color: palette.text }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveMarkdown}
                disabled={saveDisabled}
                style={{ background: palette.accent, color: palette.accentText }}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onDownload}
            disabled={isLoading || Boolean(error)}
            style={{ background: palette.accent, color: palette.accentText }}
          >
            Download
          </button>
        </footer>
      </section>
    </div>
  );
}

export default AttachmentViewerModal;
